import { Button, Card, Form, FormButtonRow } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { OrderSuppliesFormStateValidated } from '@common/validation/orderSupplies';
import type { IOrganization } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { supplyCategoriesByEpisodeCategory } from '@common/constants/supplyCategoriesByEpisodeCategory';
import { createOrderSuppliesValidationSchema } from '@common/validation/orderSupplies';
import { FormProvider } from '@frontend/components/contexts/form/FormProvider';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { OrderSuppliesFormState } from '../types/orderSuppliesFormState';
import type { OrderSuppliesRouteParams } from '../types/orderSuppliesRouteParams';
import { routeParamToEpisodeCategory, routeParamToSupplyCategory } from '../utils/orderSuppliesRouteUtils';
import { OrderSuppliesDeliveryLocation } from './form/OrderSuppliesDeliveryLocation';
import { OrderSuppliesOrder } from './form/OrderSuppliesOrder';
import { OrderSuppliesPersonalData } from './form/OrderSuppliesPersonalData';
import { OrderSuppliesPracticeData } from './form/OrderSuppliesPracticeData';

export function OrderSuppliesForm() {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const { episodeCategory: episodeCategoryParam, supplyCategory: supplyCategoryParam } =
        useParams() as OrderSuppliesRouteParams;

    const episodeCategory = episodeCategoryParam ? routeParamToEpisodeCategory(episodeCategoryParam) : undefined;
    const supplyCategory = supplyCategoryParam ? routeParamToSupplyCategory(supplyCategoryParam) : undefined;
    const authentication = useSelector((state: IReduxState) => state.authentication);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const [organization, setOrganization] = useState<IOrganization>();
    const [isLoadingOrganization, setIsLoadingOrganization] = useState(false);

    async function loadOrganization() {
        if (organizationId) {
            setIsLoadingOrganization(true);
            try {
                const url = `/api/organizations/${organizationId}`;
                const result = await abortableFetch.fetchJson(url, 'GET');
                setOrganization(result.json);
            } finally {
                setIsLoadingOrganization(false);
            }
        }
    }

    useEffect(() => {
        loadOrganization();
    }, [organizationId]);

    if (!episodeCategory || !supplyCategory) {
        return null;
    }

    const initialValues: OrderSuppliesFormState = {
        delivery: {
            city: null,
            houseNumber: null,
            pharmacy: null,
            street: null,
            zipCode: null,
        },
        order: {
            additionalNotes: null,
            amount: null,
            selectedProduct: null,
            selectedProducts: null,
        },
        personal: {
            emailAddress: authentication.emailAddress || null,
            nameFamily: authentication.nameFamily || null,
            nameGiven: authentication.nameGiven || null,
            nameInfix: authentication.nameInfix || null,
        },
        practice: {
            city: organization?.city || null,
            houseNumber: organization?.houseNumber || null,
            houseNumberSuffix: organization?.houseNumberSuffix || null,
            organizationName: organization?.name || null,
            street: organization?.street || null,
            zipCode: organization?.zip || null,
        },
    };

    const handleSubmit = async (values: OrderSuppliesFormStateValidated) => {
        try {
            const url = '/api/orderSupplies/orders';
            await abortableFetch.fetchJson(url, 'POST', {
                ...values,
                episodeCategory,
                supplyCategory,
            });
        } finally {
            navigate(`/materialenBestellen/success`);
        }
    };

    return (
        <FormProvider
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={createOrderSuppliesValidationSchema(
                supplyCategoriesByEpisodeCategory[episodeCategory]?.[supplyCategory]?.selectionMode ?? 'single',
                Boolean(supplyCategoriesByEpisodeCategory[episodeCategory]?.[supplyCategory]?.amount?.length),
                Boolean(supplyCategoriesByEpisodeCategory[episodeCategory]?.[supplyCategory]?.hasDeliveryLocation)
            )}
        >
            {(form) => (
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        form.handleSubmit(null, true);
                    }}
                    submitOnEnter={false}
                >
                    <Card>
                        <Card.Header
                            title={supplyCategoriesByEpisodeCategory[episodeCategory]?.[supplyCategory]?.label}
                        />
                        <Card.Body>
                            <OrderSuppliesPersonalData />
                            <OrderSuppliesPracticeData isLoading={isLoadingOrganization} />
                            <OrderSuppliesDeliveryLocation />
                            <OrderSuppliesOrder />
                            <FormButtonRow>
                                <Button
                                    isDisabled={form.isSubmitting}
                                    onClick={() => navigate(-1)}
                                    variant="outline"
                                >
                                    {translate.common.cancel}
                                </Button>
                                <Button
                                    isDisabled={form.isSubmitting}
                                    isLoading={form.isSubmitting}
                                    type="submit"
                                >
                                    {translate.orderSupplies.placeOrder}
                                </Button>
                            </FormButtonRow>
                        </Card.Body>
                    </Card>
                </Form>
            )}
        </FormProvider>
    );
}
